import { imageUrlFromPath } from '@lib/utils/cdnUrlFromPath'
import { neutralGray } from '@themes/Colors'
import { defaultShadow, downDefaultShadow } from '@themes/Shadows'
import * as React from 'react'
import { ActivityIndicator, StyleSheet, View } from 'react-native'
import { SolitoImage } from 'solito/image'

export type Props = {
  size?: number
  borderWidth?: number
  borderColor?: string
  margin?: number
  overlay?: boolean
  hideBorder?: boolean
  hideShadow?: boolean
  loading?: boolean
  image?: string
  alt?: string
  pressed?: boolean
  hovered?: boolean
  fallbackToGradient?: boolean
}

export function CircleImage(props: Props) {
  const {
    size = 112,
    borderWidth = 4,
    borderColor = neutralGray,
    margin = 0,
    overlay = false,
    hideBorder = false,
    hideShadow = false,
    loading = false,
    image: uri,
    alt = '',
    ...restProps
  } = props

  const width = hideBorder || overlay ? size : size - 2 * borderWidth
  const height = hideBorder || overlay ? size : size - 2 * borderWidth

  const [isLoaded, setIsLoaded] = React.useState(false)
  const [imgUrl, setImgUrl] = React.useState(uri)

  const imageRef = React.useRef(null)

  const gradients = [
    'fallback/gradient/gradient-3.png',
    'fallback/gradient/gradient-4.png',
    'fallback/gradient/gradient-6.png',
    'fallback/gradient/gradient-7.png',
    'fallback/gradient/gradient-8.png',
    'fallback/gradient/luke-chesser-CxBx_J3yp9g-unsplash.jpg',
    'fallback/gradient/luke-chesser-eICUFSeirc0-unsplash.jpg',
    'fallback/gradient/luke-chesser-IGtutkXikuc-unsplash.jpg',
    'fallback/gradient/luke-chesser-pJadQetzTkI-unsplash.jpg',
    'fallback/gradient/orange-pink-purple.png',
    'fallback/gradient/purple-blue.png',
  ]

  const randomGradient = gradients[Math.floor(Math.random() * gradients.length)]

  return (
    <View
      style={[
        styles.container,
        { width: size, height: size, borderRadius: size / 2 },
        !hideShadow && styles.shadow,
        restProps.pressed && downDefaultShadow,
        restProps.pressed && {
          transform: [
            {
              translateY: 2,
            },
          ],
        },
        !hideBorder && { borderWidth, borderColor },
      ]}
    >
      {loading ? (
        renderLoading()
      ) : (
        <>
          <View
            style={[
              styles.image,
              {
                marginTop: margin,
                width,
                height,
                borderRadius: size / 2,
              },
            ]}
          >
            <SolitoImage
              ref={imageRef}
              style={{
                opacity: isLoaded ? 1 : 0,
              }}
              unoptimized
              onLoadingComplete={({ height, width }) => {
                return setIsLoaded(true)
              }}
              onError={() => {
                setImgUrl(imageUrlFromPath(randomGradient || undefined))
                setIsLoaded(true)
              }}
              contentFit="cover"
              width={width}
              height={height}
              src={imgUrl || ''}
              alt={alt}
            />
          </View>
        </>
      )}
    </View>
  )

  function renderLoading(): React.ReactNode {
    return (
      <View
        style={[
          styles.image,
          {
            marginTop: margin,
            width,
            height,
            borderRadius: size / 2,
            justifyContent: 'center',
          },
          !hideBorder && { borderWidth, borderColor },
        ]}
      >
        <ActivityIndicator size="small" />
      </View>
    )
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  shadow: {
    ...defaultShadow,
  },
  image: {
    backgroundColor: neutralGray,
  },
})
